// submitRiskCheck.js
'use strict';
import fetch from '../resource/customFetch.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import getMeData from '../selector/getMeData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import {
  FINGERPRINTJS_PRO_API_KEY,
  FINGERPRINTJS_PRO_ENDPOINT,
  FINGERPRINTJS_PRO_REGION,
  GEETEST_FINGERPRINT_ID,
} from '../RemoteConfigKeys.js';

import raceWithTimeout from '../resource/raceWithTimeout.js';

/**
 * Submit device fingerprint data to /risk-check for fraud detection
 * @kind action
 * @param {string} {triggeredType} - trigger scenario.
 * @return {Promise} Action promise.
 */
const submitRiskCheck =
  ({ triggeredType }) =>
  async (dispatch, getState) => {
    const state = getState();
    const token = getMeData(state, 'token');
    if (!token) return dispatch({ type: '' });

    // collect geetest token and fingerprint requestId in parallel
    const [geetestResponse, fingerprintResponse] = await Promise.all([
      // geetest
      (async () => {
        const appId = getRemoteConfigData(state, GEETEST_FINGERPRINT_ID);
        if (!appId) {
          return { token: null, error: 'remote_config_geetest_app_id_missing' };
        }
        try {
          const { getGeeGuardToken } = await import('../resource/geetest.js');
          const token =
            (await raceWithTimeout({
              promise: getGeeGuardToken({ appId }),
              label: 'geetest_collect',
            })) || null;
          return {
            token,
            error: token ? null : 'geetest_token_empty_after_success',
          };
        } catch (error) {
          return {
            token: null,
            error: error?.message || 'geetest_error_without_message',
          };
        }
      })(),
      // fingerprint
      (async () => {
        const fpJsAPIKey = getRemoteConfigData(
          state,
          FINGERPRINTJS_PRO_API_KEY
        );
        if (!fpJsAPIKey) {
          return {
            requestId: null,
            error: 'remote_config_fp_pro_api_key_missing',
          };
        }
        try {
          const userId = getMeData(state, 'id');
          const clientId = getMeData(state, 'clientId');
          const fpJsEndpoint = getRemoteConfigData(
            state,
            FINGERPRINTJS_PRO_ENDPOINT
          );
          const fpJsRegion = getRemoteConfigData(
            state,
            FINGERPRINTJS_PRO_REGION
          );
          const { getFpProRequestId } = await import('../resource/fpjs.js');
          const { requestId = null } = await raceWithTimeout({
            promise: getFpProRequestId({
              fpJsAPIKey,
              fpJsEndpoint,
              fpJsRegion,
              clientId,
              userId,
            }),
            label: 'fp_pro_collect',
          });
          return {
            requestId,
            error: requestId ? null : 'fp_pro_request_id_empty_after_success',
          };
        } catch (error) {
          return {
            requestId: null,
            error: error?.message || 'fp_pro_error_without_message',
          };
        }
      })(),
    ]);

    // POST /risk-check (won't block user flow, ignore errors)
    const fetchUrl = getResourceUrl({ endpoint: '/risk-check' });
    try {
      await fetch(fetchUrl.href, {
        method: 'POST',
        headers: {
          ...getHeaders(),
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          geetest: {
            token: geetestResponse.token,
            error: geetestResponse.error,
          },
          fingerprint: {
            request_id: fingerprintResponse.requestId,
            error: fingerprintResponse.error,
          },
          triggered_type: triggeredType,
        }),
      });
    } catch {
      // ignore errors - this is a background risk check
    }
  };

export default submitRiskCheck;
